import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import VisibilitySensor from 'react-visibility-sensor';
import cx from 'classnames';
import endOfMonth from 'date-fns/end_of_month';
import format from 'date-fns/format';
import isThisMonth from 'date-fns/is_this_month';
import isThisYear from 'date-fns/is_this_year';

import {
    Flex,
    Text,
    TextButton,
    Select,
    Spacer,
} from '@zillow/constellation';
import { Container } from '@zillow/zrs-comet';

import LeadActions from 'app/shared/actions/LeadActions';
import { RMWContext } from 'app/shared/providers/RMWContext';
import { CONTACTS_PAGE } from 'app/shared/constants/RouteConstants';
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import sharedConfig from 'app/shared/sharedConfig';
import AddressColumn from 'app/shared/components/Analytics/AddressColumn';
import AnalyticsTable from 'app/shared/components/Analytics/Table';
import PermissionCheck from 'app/shared/components/PermissionCheck';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';

// Utils
import { getMonthDropdown } from 'app/shared/utils/dateUtils';
import numberUtils from 'app/shared/utils/numberUtils';
import queryUtils from 'app/shared/utils/queryUtils';

// Selectors
import { LeadSelectors } from 'app/shared/selectors';

import { StyledContainer } from './StyledComponents';

// Stylesheets
import './style.scss';

const DEFAULT_PAGE_TITLE = 'Contacts';
const MONTH_DROPDOWN = getMonthDropdown();

const propTypes = {
    allLeads: PropTypes.object.isRequired,
};

const LEADS_EXCEL_LIMIT = 10000;
const ANALYTICS_CATEGORY = 'ContactsPage';

class ContactsPage extends React.Component {
    static contextType = RMWContext;

    static contextTypes = {
        paths: PropTypes.object,
    };

    constructor(props) {
        super(props);

        this.state = {
            isLoading: true,
            isLoadingMoreLeads: false,
            startDate: MONTH_DROPDOWN[0].value,
        };

        this.handleScroll = this.handleScroll.bind(this);
        this.handleMonthChange = this.handleMonthChange.bind(this);
        this.handlePropertyClick = this.handlePropertyClick.bind(this);
        this.handleLeadClick = this.handleLeadClick.bind(this);
        this.handleDownloadClick = this.handleDownloadClick.bind(this);
    }

    componentDidMount() {
        this.getLeadsData(this.state.startDate);
    }

    handleDownloadClick() {
        const { paths } = this.context;
        const { startDate } = this.state;

        const excelPath = paths.exportExcel.leads;
        const excelParams = queryUtils.stringify({
            limit: LEADS_EXCEL_LIMIT,
            startDateYyyyMmDd: startDate,
            endDateYyyyMmDd: this.getEndDateFromStartDate(startDate),
        });
        const excelUrl = `${paths.proxy}/${sharedConfig.baseApi}${excelPath}${excelParams}`;

        this.props.dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.DOWNLOAD_DATA_CLICK, {
            category: ANALYTICS_CATEGORY,
        }));

        window.open(excelUrl);
    }

    handleMonthChange(event) {
        const startDate = event.target.value;
        this.getLeadsData(startDate);
    }

    handlePropertyClick(aliasEncoded) {
        const { paths } = this.context;

        this.props.dispatch(
            analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.PROPERTY_COLUMN_CLICK, {
                category: ANALYTICS_CATEGORY,
            })
        );

        window.router.transitionTo(`${paths.properties}/${aliasEncoded}/${paths.propertyDetailsListing}`);
    }

    handleLeadClick(event, conversationId, listingId) {
        event.stopPropagation();

        const { dispatch } = this.props;
        const { paths } = this.context;
        const queryParam = '?byProperty=true';

        dispatch(
            analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.INQUIRY_COLUMN_CLICK, {
                category: ANALYTICS_CATEGORY,
            })
        );

        window.router.updateLocation(`${paths.base}/inbox/${listingId}/${conversationId}${queryParam}`);
    }

    getEndDateFromStartDate(startDateYyyyMmDd) {
        const viewingCurrentMonth = isThisMonth(startDateYyyyMmDd) && isThisYear(startDateYyyyMmDd);
        const currentEndOfMonth = viewingCurrentMonth ? new Date() : endOfMonth(startDateYyyyMmDd);
        return format(currentEndOfMonth, 'YYYYMMDD');
    }

    getLeadsData(startDate, appendToAllLeads = false) {
        const params = {
            startDateYyyyMmDd: startDate,
            endDateYyyyMmDd: this.getEndDateFromStartDate(startDate),
        };

        if (appendToAllLeads) {
            params.start = this.props.allLeads.conversations.length;
        }

        this.setState({
            isLoading: !appendToAllLeads,
            isLoadingMoreLeads: appendToAllLeads,
            startDate,
        });

        this.props.dispatch(LeadActions.getAllLeads(params, appendToAllLeads))
            .then(() => {
                this.setState({
                    isLoading: false,
                    isLoadingMoreLeads: false,
                });
            });
    }

    getHeaderList() {
        const headerList = [
            'PROPERTY',
            'NAME',
            'EMAIL',
            'PHONE',
            'DATE',
        ];

        return headerList;
    }

    getRowList(allLeads) {
        return allLeads.conversations.map((conversation, index) => {
            const {
                address,
                inquiry,
                listingId,
                aliasEncoded,
                conversationId,
            } = conversation;

            const { name } = inquiry;
            const [street, city, state] = address.split(',');

            const listing = {
                street,
                city,
                state,
                unit: '',
                name: '',
            };

            const handleClick = ev => this.handleLeadClick(ev, conversationId, listingId);

            const rowData = {};

            rowData.property = (
                <div onClick={() => this.handlePropertyClick(aliasEncoded)}>
                    <AddressColumn
                        index={index}
                        listing={listing}
                        listingKey={listingId}
                        onClick={() => {}}
                    />
                </div>
            );

            rowData.name = (
                <div className="ContactsPage-link" onClick={handleClick}>
                    {name}
                </div>
            );

            // @TODO refactor AnalyticsTable to handle row clicks instead
            rowData.email = (
                <div className={cx('ContactsPage-link', 'ContactsPage-name')} onClick={handleClick}>
                    {inquiry.ref.indexOf('-phone') > -1 ? '[phone lead]' : inquiry.email}
                </div>
            );
            rowData.phone = (
                <div className="ContactsPage-link" onClick={handleClick}>
                    {numberUtils.formatPhoneNumber(inquiry.phone)}
                </div>
            );
            rowData.date = format(inquiry.dateSaved, 'MM/D/YYYY h:mm A');

            return rowData;
        });
    }

    getTableData(allLeads) {
        return {
            headerList: this.getHeaderList(),
            rowList: this.getRowList(allLeads),
        };
    }

    handleScroll(isVisible) {
        return isVisible && this.getLeadsData(this.state.startDate, true);
    }

    render() {
        const {
            error,
            isLoading,
            isLoadingMoreLeads,
        } = this.state;
        const { allLeads } = this.props;
        const { pageConversationCount } = allLeads;

        const {
            headerList,
            rowList,
        } = this.getTableData(allLeads);

        return (
            <AuthCheckWrapper requireStrongAuthentication={false}>
                <PermissionCheck route={CONTACTS_PAGE}>
                    <div className="ContactsPage">
                        <HeadWrapper title={DEFAULT_PAGE_TITLE} />
                        <Container className="ContactsPage-container">
                            <StyledContainer className="ContactsPage-card">
                                <Spacer paddingY="md">
                                    <Text fontType="h3">
                                        {DEFAULT_PAGE_TITLE}
                                    </Text>
                                </Spacer>
                                <Spacer marginY="sm">
                                    <Flex display="flex" justifyContent="space-between">
                                        <Select
                                            disabled={isLoading}
                                            name="startDate"
                                            onChange={this.handleMonthChange}
                                            fluid={false}
                                        >
                                            {MONTH_DROPDOWN.map(({ value, name }) => (
                                                <option key={value} value={value}>{name}</option>
                                            ))}
                                        </Select>
                                        {!isLoading && (
                                            <TextButton onClick={this.handleDownloadClick}>
                                                Export message history
                                            </TextButton>
                                        )}
                                    </Flex>
                                </Spacer>
                                <AnalyticsTable
                                    headerList={headerList}
                                    rowList={rowList}
                                    errorMessage={error}
                                    isLoading={isLoading}
                                    emptyMessage="No contacts found."
                                    alignRight={false}
                                />

                                {
                                    isLoadingMoreLeads && <SpinnerContainer />
                                }

                                {
                                    pageConversationCount === 10 && (
                                        <VisibilitySensor
                                            onChange={this.handleScroll}
                                            offset={{ top: 100 }}
                                        />
                                    )
                                }
                            </StyledContainer>
                        </Container>
                    </div>
                </PermissionCheck>
            </AuthCheckWrapper>
        );
    }
}

const mapStateToProps = state => ({
    allLeads: LeadSelectors.makeAllLeads(state),
});

ContactsPage.propTypes = propTypes;

export { ContactsPage };
export default connect(mapStateToProps)(ContactsPage);
